import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { User, Lock, CreditCard, Mail, Trash2, Download, AlertTriangle, Loader2, ChevronRight, UserCog, Plus, ExternalLink, RefreshCw } from 'lucide-react';

/**
 * /account — single page for everything a parent needs to self-serve:
 *   1. Profile (display name, country)
 *   2. Password
 *   3. Marketing consent
 *   4. Subscription summary + cancel
 *   5. Child profile management
 *   6. GDPR data export
 *   7. Delete account (irreversible)
 */
export default function Account() {
    const { user, refreshUser, children, refreshChildren, logout } = useAuth();
    const navigate = useNavigate();
    const [sub, setSub] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/me/subscription');
                if (cancelled) return;
                setSub(data);
                // Self-heal: if the live subscription is active/trialing on a paid
                // tier but the cached user fields are stale (no tier or wrong
                // status), silently call /me/subscription/sync so the cap checks
                // see correct data without the user having to click anything.
                const liveTier = (data?.subscription?.tier || '').toLowerCase();
                const liveStatus = (data?.subscription?.status || '').toLowerCase();
                const cachedTier = (user.subscription_tier || '').toLowerCase();
                const cachedStatus = (user.subscription_status || '').toLowerCase();
                const livePaid = ['active', 'trialing'].includes(liveStatus) &&
                    ['explorer', 'polyglot', 'global'].includes(liveTier);
                const cachedMatches = cachedTier === liveTier &&
                    ['active', 'trialing'].includes(cachedStatus);
                if (livePaid && !cachedMatches) {
                    try {
                        await api.post('/me/subscription/sync');
                        await refreshUser?.();
                    } catch { /* non-fatal — the UI already uses live sub */ }
                }
            } catch { /* ignore — page handles missing sub */ }
        })();
        return () => { cancelled = true; };
    }, [user, refreshUser]);

    if (!user) {
        return <main className="max-w-4xl mx-auto px-6 py-16">Please sign in.</main>;
    }

    return (
        <main className="max-w-4xl mx-auto px-6 py-12" data-testid="page-account">
            <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500">Account</p>
            <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">My account</h1>
            <p className="mt-3 text-lq-ink-700">Manage your sign-in, subscription, child profiles and data. Most changes take effect immediately.</p>

            <ProfileSection user={user} refreshUser={refreshUser} />
            <PasswordSection />
            <MarketingSection user={user} refreshUser={refreshUser} />
            <SubscriptionSection sub={sub} setSub={setSub} user={user} refreshUser={refreshUser} refreshChildren={refreshChildren} />
            <ChildrenSection children={children} refreshChildren={refreshChildren} user={user} sub={sub} />
            <DataExportSection user={user} />
            <DangerZone user={user} logout={logout} navigate={navigate} />
        </main>
    );
}

function Section({ icon: Icon, title, subtitle, children, testid }) {
    return (
        <section className="mt-10 bg-white border border-lq-ink-100 rounded-2xl p-6" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-purple-100 text-lq-purple-700 flex-shrink-0">
                    <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">{title}</h2>
                    {subtitle && <p className="mt-1 text-sm text-lq-ink-500">{subtitle}</p>}
                    <div className="mt-4">{children}</div>
                </div>
            </div>
        </section>
    );
}

function ProfileSection({ user, refreshUser }) {
    const [name, setName] = useState(user.display_name || '');
    const [busy, setBusy] = useState(false);
    const dirty = name.trim() !== (user.display_name || '').trim();

    const save = async () => {
        const trimmed = name.trim();
        if (trimmed.length < 2) { toast.error('Name must be at least 2 characters'); return; }
        setBusy(true);
        try {
            await api.patch('/me/display-name', { display_name: trimmed });
            await refreshUser?.();
            toast.success('Name updated');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save');
        } finally { setBusy(false); }
    };

    return (
        <Section icon={User} title="Profile" subtitle="What other family members and your child profiles see." testid="account-profile">
            <label className="block text-sm font-medium text-lq-ink-900">Display name</label>
            <div className="mt-1 flex flex-wrap gap-2">
                <input className="rounded-lg border border-lq-ink-300 px-3 py-2 flex-1 min-w-[200px]" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-testid="account-name-input" />
                <button onClick={save} disabled={!dirty || busy} className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 disabled:opacity-50" data-testid="account-name-save">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
                </button>
            </div>
            <dl className="mt-5 grid sm:grid-cols-2 gap-y-2 text-sm">
                <dt className="text-lq-ink-500">Email</dt>
                <dd className="text-lq-ink-900 break-all">{user.email}</dd>
                <dt className="text-lq-ink-500">Country</dt>
                <dd className="text-lq-ink-900">{user.country_code || 'GB'}</dd>
                <dt className="text-lq-ink-500">Role</dt>
                <dd className="text-lq-ink-900 capitalize">{user.role}</dd>
                <dt className="text-lq-ink-500">Member since</dt>
                <dd className="text-lq-ink-900">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</dd>
            </dl>
        </Section>
    );
}

function PasswordSection() {
    const [cur, setCur] = useState('');
    const [next, setNext] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
        setBusy(true);
        try {
            await api.post('/me/password', { current_password: cur, new_password: next });
            toast.success('Password updated');
            setCur(''); setNext('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update password');
        } finally { setBusy(false); }
    };

    return (
        <Section icon={Lock} title="Password" subtitle="Choose something at least 8 characters long. We never email passwords." testid="account-password">
            <form onSubmit={submit} className="space-y-3 max-w-md">
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Current password</span>
                    <input type="password" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" data-testid="account-pw-current" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">New password</span>
                    <input type="password" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="account-pw-new" />
                </label>
                <button disabled={busy || !cur || !next} className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 disabled:opacity-50" data-testid="account-pw-save">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : 'Update password'}
                </button>
            </form>
        </Section>
    );
}

function MarketingSection({ user, refreshUser }) {
    const [on, setOn] = useState(!!user.marketing_email_consent);
    const [busy, setBusy] = useState(false);

    const toggle = async () => {
        const next = !on;
        setBusy(true);
        try {
            await api.patch('/me/marketing', { marketing_email_consent: next });
            await refreshUser?.();
            setOn(next);
            toast.success(next ? 'You will receive product updates' : 'Marketing emails turned off');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update');
        } finally { setBusy(false); }
    };

    return (
        <Section icon={Mail} title="Email preferences" subtitle="We always send transactional emails (receipts, password resets). Other emails are opt-in." testid="account-marketing">
            <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={toggle} disabled={busy} data-testid="account-marketing-toggle" className="w-5 h-5 mt-0.5" />
                    <span className="text-sm text-lq-ink-900">
                        <strong>Product updates &amp; new languages</strong> — occasional notes about new features and languages, max ~1 per month.
                    </span>
                </label>
                <WeeklyRecapToggle user={user} refreshUser={refreshUser}/>
            </div>
        </Section>
    );
}


/** Family weekly recap — separate toggle. Defaults to ON because it's a
 * product email celebrating the family's own activity, not marketing. */
function WeeklyRecapToggle({ user, refreshUser }) {
    // Default ON unless the user explicitly opted out.
    const [on, setOn] = useState(user.weekly_recap_consent !== false);
    const [busy, setBusy] = useState(false);
    const toggle = async () => {
        const next = !on;
        setBusy(true);
        try {
            await api.patch('/me/weekly-recap', { weekly_recap_consent: next });
            await refreshUser?.();
            setOn(next);
            toast.success(next ? 'Weekly recaps turned on' : 'Weekly recaps paused');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update');
        } finally { setBusy(false); }
    };
    return (
        <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={on} onChange={toggle} disabled={busy} data-testid="account-weekly-recap-toggle" className="w-5 h-5 mt-0.5" />
            <span className="text-sm text-lq-ink-900">
                <strong>Sunday family recap</strong> — a quiet weekly snapshot of what your children enjoyed (no comparisons, no streak shaming). <em>Pace, not pressure.</em>
            </span>
        </label>
    );
}

function SubscriptionSection({ sub, setSub, user, refreshUser, refreshChildren }) {
    const [busy, setBusy] = useState(false);
    const liveSub = sub?.subscription;
    const isPaid = liveSub && ['active', 'trialing'].includes(liveSub.status);
    const cancelled = liveSub?.cancel_at_period_end;
    const inTrial = liveSub?.status === 'trialing';
    const trialEnd = liveSub?.trial_ends_at;
    const periodEnd = liveSub?.current_period_end;
    const price = sub?.price;

    const refetch = async () => {
        const { data } = await api.get('/me/subscription');
        setSub(data);
        await refreshUser?.();
    };

    const syncFromPayPal = async () => {
        setBusy(true);
        try {
            const { data: synced } = await api.post('/me/subscription/sync');
            await refetch();
            await refreshChildren?.();
            const tierLabel = (synced?.tier || '').toUpperCase();
            toast.success(tierLabel ? `Synced — your ${tierLabel} plan is active.` : 'Subscription state refreshed from PayPal');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not sync with PayPal');
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        if (!window.confirm('Cancel your subscription at the end of the current period? You will keep access until the period ends.')) return;
        setBusy(true);
        try {
            await api.post('/me/subscription/cancel');
            await refetch();
            toast.success('Subscription will not renew');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not cancel');
        } finally { setBusy(false); }
    };

    const reactivate = async () => {
        setBusy(true);
        try {
            await api.post('/me/subscription/reactivate');
            await refetch();
            toast.success('Subscription will continue to renew');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not reactivate');
        } finally { setBusy(false); }
    };

    const fmtDate = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
        catch { return '—'; }
    };

    return (
        <Section icon={CreditCard} title="Subscription" subtitle="Your plan, billing dates, and cancellation." testid="account-subscription">
            {!sub ? <div className="text-sm text-lq-ink-500">Loading…</div> : (
                <>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Stat label="Plan" value={(liveSub?.tier || sub.subscription_tier || 'free').toUpperCase()} testid="account-sub-tier" />
                        <Stat label="Status" value={statusLabel(liveSub, sub.subscription_status)} testid="account-sub-status" />
                        <Stat label={cancelled ? 'Ends on' : (inTrial ? 'Trial ends' : 'Next billing')}
                              value={fmtDate(cancelled ? periodEnd : (inTrial ? trialEnd : periodEnd))}
                              testid="account-sub-date" />
                    </div>

                    {isPaid && price && (
                        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm bg-lq-paper border border-lq-ink-100 rounded-xl px-4 py-3">
                            <Field label="Price" value={`${price.amount_label} / ${price.billing_period === 'annual' ? 'year' : 'month'}`} testid="account-sub-price" />
                            <Field label="Billing period" value={price.billing_period === 'annual' ? 'Annual' : 'Monthly'} testid="account-sub-period" />
                            <Field label="Payment provider" value="PayPal" testid="account-sub-provider" />
                            {liveSub?.provider_subscription_id && (
                                <Field label="Subscription ID" value={liveSub.provider_subscription_id} testid="account-sub-provider-id" mono />
                            )}
                            <Field label="Started" value={fmtDate(liveSub?.created_at)} testid="account-sub-started" />
                        </div>
                    )}

                    {cancelled && (
                        <div className="mt-4 bg-lq-warn/10 border border-lq-warn/30 rounded-xl px-4 py-3 text-sm text-lq-ink-900" data-testid="account-sub-cancel-banner">
                            <strong>Your subscription is set to end on {fmtDate(periodEnd)}.</strong> You can keep using paid features until then. Change your mind? Reactivate below.
                        </div>
                    )}

                    <div className="mt-5 flex flex-wrap gap-3">
                        {!isPaid && (
                            <Link to="/pricing" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="account-sub-upgrade">
                                Upgrade plan →
                            </Link>
                        )}
                        {!isPaid && (
                            <Link to="/redeem" className="px-5 py-2 rounded-full bg-white border-2 border-lq-purple-500 text-lq-purple-700 font-semibold hover:bg-lq-purple-50 inline-flex items-center gap-2" data-testid="account-sub-redeem" title="Redeem a Linguistic Quest gift card">
                                Redeem a gift card
                            </Link>
                        )}
                        {isPaid && !cancelled && (
                            <button onClick={cancel} disabled={busy} className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 disabled:opacity-50" data-testid="account-sub-cancel">
                                {busy ? 'Cancelling…' : 'Cancel subscription'}
                            </button>
                        )}
                        {isPaid && cancelled && (
                            <button onClick={reactivate} disabled={busy} className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700 disabled:opacity-50" data-testid="account-sub-reactivate">
                                {busy ? 'Reactivating…' : 'Reactivate subscription'}
                            </button>
                        )}
                        {isPaid && sub?.paypal_manage_url && (
                            <a href={sub.paypal_manage_url} target="_blank" rel="noopener noreferrer"
                               className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 inline-flex items-center gap-1"
                               data-testid="account-sub-paypal-manage">
                                Manage in PayPal <ExternalLink size={14} />
                            </a>
                        )}
                        <button
                            onClick={syncFromPayPal}
                            disabled={busy}
                            className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="account-sub-sync"
                            title="Re-fetch your subscription state from PayPal if anything looks out of date"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh from PayPal
                        </button>
                    </div>

                    <p className="mt-3 text-xs text-lq-ink-500">
                        Subscription state is synced automatically when you subscribe. If anything looks out of date, hit "Refresh from PayPal" — it pulls live status straight from PayPal.
                    </p>

                    {sub.history?.length > 1 && (
                        <details className="mt-6" data-testid="account-sub-history">
                            <summary className="text-sm text-lq-purple-700 hover:underline cursor-pointer">View subscription history ({sub.history.length})</summary>
                            <ul className="mt-3 divide-y divide-lq-ink-100 bg-lq-paper border border-lq-ink-100 rounded-xl text-sm">
                                {sub.history.map((h) => (
                                    <li key={h.id} className="px-4 py-2 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium text-lq-ink-900">{(h.tier || '').toUpperCase()} · {h.billing_period || 'monthly'}</div>
                                            <div className="text-xs text-lq-ink-500">Started {fmtDate(h.created_at)} · Status: {h.status}</div>
                                        </div>
                                        {h.provider_subscription_id && (
                                            <span className="font-mono text-xs text-lq-ink-500 truncate max-w-[140px]" title={h.provider_subscription_id}>{h.provider_subscription_id}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
        </Section>
    );
}

function statusLabel(liveSub, fallback) {
    if (!liveSub) return fallback || 'none';
    if (liveSub.cancel_at_period_end) return 'cancels at period end';
    return liveSub.status || fallback || 'none';
}

function Field({ label, value, testid, mono }) {
    return (
        <div data-testid={testid} className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-lq-ink-500">{label}</div>
            <div className={`mt-0.5 text-sm text-lq-ink-900 ${mono ? 'font-mono' : ''} truncate`} title={value}>{value}</div>
        </div>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="bg-lq-paper border border-lq-ink-100 rounded-xl px-4 py-3" data-testid={testid}>
            <div className="text-xs uppercase tracking-wide text-lq-ink-500">{label}</div>
            <div className="mt-1 text-base text-lq-ink-900 font-semibold">{value}</div>
        </div>
    );
}

function ChildrenSection({ children, refreshChildren, user, sub }) {
    const [busy, setBusy] = useState('');
    const liveSub = sub?.subscription;
    const isPaid = liveSub && ['active', 'trialing'].includes(liveSub.status);
    // Prefer the LIVE subscription row's tier over the cached user field —
    // the cached field can lag if the activation webhook didn't write it.
    const tier = (liveSub?.tier || user.subscription_tier || '').toLowerCase();
    const cap = isPaid ? ({ explorer: 3, polyglot: 4, global: 5 }[tier] || 1) : 1;

    const remove = async (c) => {
        if (!window.confirm(`Remove ${c.display_name}? Their progress will be archived.`)) return;
        setBusy(c.id);
        try {
            await api.delete(`/me/children/${c.id}`);
            await refreshChildren?.();
            toast.success(`${c.display_name} removed`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not remove');
        } finally { setBusy(''); }
    };

    return (
        <Section icon={UserCog} title="Child profiles" subtitle={`${children.length} of ${cap} profile${cap === 1 ? '' : 's'} on your current plan.`} testid="account-children">
            <div className="mb-5 bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-5" data-testid="account-children-howto">
                <h3 className="font-display text-base font-semibold text-lq-ink-900">How your child plays — and why there is no child login</h3>
                <ol className="mt-3 space-y-2 text-sm text-lq-ink-700 leading-relaxed list-decimal pl-5">
                    <li><strong>You sign in</strong> with your parent email and password — that is the family account.</li>
                    <li>Open <Link to="/dashboard" className="text-lq-purple-700 hover:underline">your dashboard</Link>. At the top, a small pill shows every child&rsquo;s nickname.</li>
                    <li><strong>Tap a child&rsquo;s name</strong> to make their profile active. Every puzzle, point and saved word now counts towards that child.</li>
                    <li>To switch to a sibling, just tap their name in the same pill. To finish, sign out from the top-right menu.</li>
                </ol>
                <p className="mt-3 text-sm text-lq-ink-700 leading-relaxed">
                    <strong>Why no separate child login?</strong> Three reasons we feel strongly about:
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-lq-ink-700 leading-relaxed list-disc pl-5">
                    <li><strong>Child safety.</strong> The fewer accounts a child has, the smaller their digital footprint. We follow the UK ICO Children&rsquo;s Code, which recommends minimising data collected from minors.</li>
                    <li><strong>Family-led learning.</strong> Heritage languages survive when they are shared, not drilled — Linguistic Quest is built around a parent, carer or grandparent sitting next to the child, not around the child playing solo with their own login.</li>
                    <li><strong>You stay in control.</strong> Only you can add a child, switch a language, change difficulty, see billing or cancel. Nobody on a child profile can change anything about the family account.</li>
                </ul>
                <p className="mt-3 text-xs text-lq-ink-500">
                    Schools tier is the one exception &mdash; students log in directly with a class join code, nickname and 4-digit PIN, still without an email.
                </p>
            </div>
            {children.length === 0 && <p className="text-sm text-lq-ink-500">No child profiles yet.</p>}
            <ul className="space-y-2">
                {children.map((c) => (
                    <li key={c.id} className="flex items-center justify-between bg-lq-paper border border-lq-ink-100 rounded-xl px-4 py-3" data-testid={`account-child-${c.id}`}>
                        <div className="min-w-0">
                            <div className="font-medium text-lq-ink-900 truncate">{c.display_name}</div>
                            <div className="text-xs text-lq-ink-500">Age {c.age} · {c.preferred_difficulty || 'auto'} · {c.age_tier_slug}</div>
                        </div>
                        <button onClick={() => remove(c)} disabled={busy === c.id} className="text-sm text-lq-error hover:underline disabled:opacity-50" data-testid={`account-child-remove-${c.id}`}>
                            {busy === c.id ? 'Removing…' : 'Remove'}
                        </button>
                    </li>
                ))}
            </ul>
            <div className="mt-4">
                {children.length < cap ? (
                    <Link to="/onboarding/add-child" className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="account-add-child">
                        <Plus size={14}/> Add a child
                    </Link>
                ) : (
                    <Link to="/pricing" className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100" data-testid="account-upgrade-for-children">
                        Upgrade for more child profiles <ChevronRight size={14}/>
                    </Link>
                )}
            </div>
        </Section>
    );
}

function DataExportSection() {
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        try {
            const { data } = await api.get('/me/data-export');
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `linguistic-quest-data-export-${new Date().toISOString().slice(0,10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Export downloaded');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Export failed');
        } finally { setBusy(false); }
    };

    return (
        <Section icon={Download} title="Download your data" subtitle="A complete JSON copy of everything we hold under your account, including child profiles and progress (UK GDPR Article 15)." testid="account-export">
            <button onClick={download} disabled={busy} className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100 disabled:opacity-50 inline-flex items-center gap-2" data-testid="account-export-download">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download JSON
            </button>
        </Section>
    );
}

function DangerZone({ user, logout, navigate }) {
    // 3-step flow: entry → save-attempt (reason + alt options) → final confirm.
    const [step, setStep] = useState('closed'); // 'closed' | 'reason' | 'confirm'
    const [reason, setReason] = useState('');
    const [feedback, setFeedback] = useState('');
    const [pw, setPw] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);

    const REASONS = [
        { slug: 'too_expensive',   label: 'Too expensive' },
        { slug: 'not_using',       label: 'Not using it enough' },
        { slug: 'child_lost_interest', label: 'My child lost interest' },
        { slug: 'bugs_issues',     label: 'Bugs or technical issues' },
        { slug: 'privacy',         label: 'Privacy concerns' },
        { slug: 'other',           label: 'Other' },
    ];

    const cancelOnly = async () => {
        setBusy(true);
        try {
            await api.post('/me/save-attempt/cancel-only', { reason, feedback });
            toast.success('Paid subscription cancelled. Your account is safe and you can keep using the free tier.');
            setStep('closed'); setReason(''); setFeedback(''); setPw(''); setConfirm('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not cancel subscription');
        } finally { setBusy(false); }
    };

    const wipe = async () => {
        setBusy(true);
        try {
            await api.delete('/me', { data: { password: pw, confirm, reason, feedback } });
            toast.success('Account deleted. We are sorry to see you go.');
            logout?.();
            navigate('/');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete account');
        } finally { setBusy(false); }
    };

    return (
        <section className="mt-10 bg-white border-2 border-lq-error/30 rounded-2xl p-6" data-testid="account-danger">
            <div className="flex items-start gap-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-error/10 text-lq-error">
                    <AlertTriangle size={18} />
                </div>
                <div className="flex-1">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">Delete account</h2>
                    <p className="mt-1 text-sm text-lq-ink-500">Permanently removes your account and all child profiles. Subscriptions are cancelled at the end of the current billing period. This cannot be undone after 30 days.</p>

                    {step === 'closed' && (
                        <button onClick={() => setStep('reason')} className="mt-4 px-5 py-2 rounded-full bg-white border-2 border-lq-error/40 text-lq-error font-semibold hover:bg-lq-error/5" data-testid="account-delete-open">
                            <Trash2 size={14} className="inline mr-1" /> I want to delete my account
                        </button>
                    )}

                    {step === 'reason' && (
                        <div className="mt-4 space-y-5 max-w-2xl" data-testid="account-delete-reason-step">
                            <div>
                                <div className="text-sm font-semibold text-lq-ink-900 mb-2">Before you go — help us understand why?</div>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {REASONS.map((r) => (
                                        <label key={r.slug} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${reason === r.slug ? 'border-lq-purple-500 bg-lq-purple-50 text-lq-purple-900' : 'border-lq-ink-100 hover:border-lq-ink-300'}`}>
                                            <input type="radio" name="delete-reason" value={r.slug} checked={reason === r.slug} onChange={(e) => setReason(e.target.value)} data-testid={`account-delete-reason-${r.slug}`} className="accent-lq-purple-500"/>
                                            <span>{r.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <label className="block">
                                <span className="text-sm font-medium text-lq-ink-900">Anything you'd like to tell us? (optional)</span>
                                <textarea
                                    className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 text-sm"
                                    rows={3}
                                    maxLength={2000}
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="What could we have done better?"
                                    data-testid="account-delete-feedback"
                                />
                            </label>

                            <div className="bg-lq-peach-50 border border-lq-peach-200 rounded-xl p-4">
                                <div className="text-sm font-semibold text-lq-peach-900">Would either of these work instead?</div>
                                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                    <button
                                        onClick={cancelOnly}
                                        disabled={busy}
                                        className="px-4 py-2 rounded-full bg-lq-purple-500 text-white text-sm font-semibold hover:bg-lq-purple-700 disabled:opacity-50"
                                        data-testid="account-save-cancel-only"
                                    >
                                        {busy && <Loader2 size={14} className="inline animate-spin mr-1"/>} Cancel my paid plan, keep my account (free tier)
                                    </button>
                                    <Link
                                        to="/contact"
                                        className="px-4 py-2 rounded-full bg-white border border-lq-peach-500 text-lq-peach-900 text-sm font-semibold hover:bg-lq-peach-100 inline-flex items-center gap-1"
                                        data-testid="account-save-contact"
                                    >
                                        Contact support instead
                                    </Link>
                                </div>
                                <div className="mt-2 text-xs text-lq-ink-500">Cancelling keeps your progress, child profiles and account intact — you just stop being billed.</div>
                            </div>

                            <div className="flex gap-2 pt-1">
                                <button
                                    onClick={() => setStep('confirm')}
                                    disabled={!reason}
                                    className="px-5 py-2 rounded-full bg-white border-2 border-lq-error/40 text-lq-error font-semibold hover:bg-lq-error/5 disabled:opacity-50"
                                    data-testid="account-delete-continue"
                                >
                                    Continue with deletion
                                </button>
                                <button
                                    onClick={() => { setStep('closed'); setReason(''); setFeedback(''); }}
                                    className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100"
                                    data-testid="account-delete-back"
                                >
                                    Back
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'confirm' && (
                        <div className="mt-4 space-y-3 max-w-md" data-testid="account-delete-confirm-step">
                            <label className="block">
                                <span className="text-sm font-medium text-lq-ink-900">Your password</span>
                                <input type="password" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" data-testid="account-delete-pw" />
                            </label>
                            <label className="block">
                                <span className="text-sm font-medium text-lq-ink-900">Type DELETE in capital letters</span>
                                <input className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="account-delete-confirm" />
                            </label>
                            <div className="flex gap-2">
                                <button onClick={wipe} disabled={busy || !pw || confirm !== 'DELETE'} className="px-5 py-2 rounded-full bg-lq-error text-white font-semibold hover:opacity-90 disabled:opacity-50" data-testid="account-delete-submit">
                                    {busy ? 'Deleting…' : 'Delete forever'}
                                </button>
                                <button onClick={() => { setStep('reason'); setPw(''); setConfirm(''); }} className="px-5 py-2 rounded-full bg-white border border-lq-ink-300 text-lq-ink-900 hover:bg-lq-ink-100" data-testid="account-delete-cancel">Back</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
